<template>
  <div class="html-editor-wrap" :class="{ 'html-editor-wrap--focused': isFocused }">
    <!-- Plain mode: simple textarea, no toolbar -->
    <v-textarea
      v-if="isPlain"
      :value="value"
      :label="label"
      :hint="hint"
      :outlined="outlined"
      :dense="dense"
      :readonly="readOnly"
      @input="$emit('input', $event)"
    />

    <div v-else class="html-editor-control" style="position: relative">
      <div v-if="label && outlined" style="position: absolute; height: 0" class="v-text-field__slot">
        <label
          style="position: relative; left: 10px; bottom: 11px; z-index: 1; font-size: 12px"
          :style="`${label ? 'padding: 2px;' : ''} background-color: ${
            !$vuetify.theme.dark ? 'white' : 'rgb(30, 30, 30)'
          };`"
          class="v-label"
          :class="isFocused ? 'primary--text' : ''"
        >
          {{ label }}
        </label>
      </div>
      <div
        v-else-if="label"
        class="v-label html-editor-label"
        :class="$vuetify.theme.dark ? 'theme--dark' : 'theme--light'"
      >
        {{ label }}
      </div>

      <!-- Formatting toolbar -->
      <div
        v-if="editor && !readOnly"
        class="html-toolbar d-flex align-center flex-wrap px-1"
        :class="{ 'html-toolbar--focused': isFocused }"
      >
        <!-- Font size -->
        <v-menu offset-y :close-on-content-click="true">
          <template #activator="{ on: menuOn, attrs }">
            <v-tooltip bottom>
              <template #activator="{ on: tooltipOn }">
                <v-btn icon x-small tabindex="-1" v-bind="attrs" v-on="{ ...menuOn, ...tooltipOn }" @mousedown.prevent>
                  <v-icon small>mdi-format-size</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("components.utility.HtmlEditor.fontSize") }}</span>
            </v-tooltip>
          </template>
          <v-list dense>
            <v-list-item @mousedown.prevent @click="editor.chain().focus().unsetFontSize().run()">
              <v-list-item-title>{{ $t("components.utility.HtmlEditor.fontSizeNormal") }}</v-list-item-title>
            </v-list-item>
            <v-list-item @mousedown.prevent @click="editor.chain().focus().setFontSize('0.85em').run()">
              <v-list-item-title>{{ $t("components.utility.HtmlEditor.fontSizeSmall") }}</v-list-item-title>
            </v-list-item>
            <v-list-item @mousedown.prevent @click="editor.chain().focus().setFontSize('1.4em').run()">
              <v-list-item-title>{{ $t("components.utility.HtmlEditor.fontSizeLarge") }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Text color -->
        <v-menu offset-y :close-on-content-click="true">
          <template #activator="{ on: menuOn, attrs }">
            <v-tooltip bottom>
              <template #activator="{ on: tooltipOn }">
                <v-btn icon x-small tabindex="-1" v-bind="attrs" v-on="{ ...menuOn, ...tooltipOn }" @mousedown.prevent>
                  <v-icon small>mdi-format-color-text</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("components.utility.HtmlEditor.textColor") }}</span>
            </v-tooltip>
          </template>
          <v-card class="pa-2" style="max-width: 160px">
            <div class="d-flex flex-wrap" style="gap: 4px">
              <div
                v-for="color in colors"
                :key="color"
                :style="{
                  backgroundColor: color,
                  width: '20px',
                  height: '20px',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  border: '1px solid rgba(0,0,0,0.12)'
                }"
                @mousedown.prevent
                @click="editor.chain().focus().setColor(color).run()"
              />
            </div>
            <v-divider class="my-1" />
            <v-list-item dense @mousedown.prevent @click="editor.chain().focus().unsetColor().run()">
              <v-list-item-title>{{ $t("components.utility.HtmlEditor.removeColor") }}</v-list-item-title>
            </v-list-item>
          </v-card>
        </v-menu>

        <!-- Highlight / background color -->
        <v-menu offset-y :close-on-content-click="true">
          <template #activator="{ on: menuOn, attrs }">
            <v-tooltip bottom>
              <template #activator="{ on: tooltipOn }">
                <v-btn icon x-small tabindex="-1" v-bind="attrs" v-on="{ ...menuOn, ...tooltipOn }" @mousedown.prevent>
                  <v-icon small>mdi-format-color-highlight</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("components.utility.HtmlEditor.highlightColor") }}</span>
            </v-tooltip>
          </template>
          <v-card class="pa-2" style="max-width: 160px">
            <div class="d-flex flex-wrap" style="gap: 4px">
              <div
                v-for="color in colors"
                :key="color"
                :style="{
                  backgroundColor: color,
                  width: '20px',
                  height: '20px',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  border: '1px solid rgba(0,0,0,0.12)'
                }"
                @mousedown.prevent
                @click="editor.chain().focus().toggleHighlight({ color }).run()"
              />
            </div>
            <v-divider class="my-1" />
            <v-list-item dense @mousedown.prevent @click="editor.chain().focus().unsetHighlight().run()">
              <v-list-item-title>{{ $t("components.utility.HtmlEditor.removeColor") }}</v-list-item-title>
            </v-list-item>
          </v-card>
        </v-menu>

        <v-divider vertical class="mx-1 toolbar-divider" />

        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              tabindex="-1"
              v-bind="attrs"
              v-on="on"
              :class="{ 'toolbar-btn--active': editor.isActive('bold') }"
              @mousedown.prevent
              @click="editor.chain().focus().toggleBold().run()"
            >
              <v-icon small>mdi-format-bold</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.utility.MarkdownEditor.bold") }} ({{ modKey }}+B)</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              tabindex="-1"
              v-bind="attrs"
              v-on="on"
              :class="{ 'toolbar-btn--active': editor.isActive('italic') }"
              @mousedown.prevent
              @click="editor.chain().focus().toggleItalic().run()"
            >
              <v-icon small>mdi-format-italic</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.utility.MarkdownEditor.italic") }} ({{ modKey }}+I)</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              tabindex="-1"
              v-bind="attrs"
              v-on="on"
              :class="{ 'toolbar-btn--active': editor.isActive('underline') }"
              @mousedown.prevent
              @click="editor.chain().focus().toggleUnderline().run()"
            >
              <v-icon small>mdi-format-underline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.utility.MarkdownEditor.underline") }} ({{ modKey }}+U)</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              tabindex="-1"
              v-bind="attrs"
              v-on="on"
              :class="{ 'toolbar-btn--active': editor.isActive('strike') }"
              @mousedown.prevent
              @click="editor.chain().focus().toggleStrike().run()"
            >
              <v-icon small>mdi-format-strikethrough</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.utility.MarkdownEditor.strikethrough") }} ({{ modKey }}+Shift+S)</span>
        </v-tooltip>

        <v-divider vertical class="mx-1 toolbar-divider" />

        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              tabindex="-1"
              v-bind="attrs"
              v-on="on"
              :class="{ 'toolbar-btn--active': editor.isActive('link') }"
              @mousedown.prevent
              @click="setLink"
            >
              <v-icon small>mdi-link-variant</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.utility.HtmlEditor.link") }}</span>
        </v-tooltip>

        <v-divider vertical class="mx-1 toolbar-divider" />

        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              tabindex="-1"
              v-bind="attrs"
              v-on="on"
              :class="{ 'toolbar-btn--active': editor.isActive('bulletList') }"
              @mousedown.prevent
              @click="editor.chain().focus().toggleBulletList().run()"
            >
              <v-icon small>mdi-format-list-bulleted</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.utility.MarkdownEditor.bulletList") }} ({{ modKey }}+Shift+8)</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              tabindex="-1"
              v-bind="attrs"
              v-on="on"
              :class="{ 'toolbar-btn--active': editor.isActive('orderedList') }"
              @mousedown.prevent
              @click="editor.chain().focus().toggleOrderedList().run()"
            >
              <v-icon small>mdi-format-list-numbered</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.utility.MarkdownEditor.numberedList") }} ({{ modKey }}+Shift+7)</span>
        </v-tooltip>

        <template v-if="canUploadImage">
          <v-divider vertical class="mx-1 toolbar-divider" />
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                x-small
                tabindex="-1"
                v-bind="attrs"
                v-on="on"
                :loading="isUploading"
                @click="triggerImageUpload"
              >
                <v-icon small>mdi-image-plus-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("components.utility.MarkdownEditor.uploadImage") }}</span>
          </v-tooltip>
        </template>

        <template v-if="availableTokens.length">
          <v-divider vertical class="mx-1 toolbar-divider" />
          <v-menu offset-y :close-on-content-click="true">
            <template #activator="{ on: menuOn, attrs }">
              <v-tooltip bottom>
                <template #activator="{ on: tipOn }">
                  <v-btn
                    text
                    x-small
                    tabindex="-1"
                    color="primary"
                    v-bind="attrs"
                    v-on="{ ...menuOn, ...tipOn }"
                    class="px-1"
                    @mousedown.prevent
                  >
                    <v-icon small class="mr-1">mdi-plus</v-icon>
                    {{ $t("components.utility.HtmlEditor.placeholder") }}
                  </v-btn>
                </template>
                <span>{{ $t("components.utility.HtmlEditor.placeholderTooltip") }}</span>
              </v-tooltip>
            </template>
            <v-list dense max-height="320" class="overflow-y-auto">
              <v-list-item v-for="token of availableTokens" :key="token" @click="insertPlaceholder(token)">
                <v-list-item-title>{{ tokenLabel(token) }}</v-list-item-title>
                <v-list-item-subtitle>{{ token }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <slot name="actions" />
      </div>

      <editor-content
        :editor="editor"
        class="html-editor rounded"
        :class="{
          'html-editor--readonly': readOnly,
          'html-editor--focused': isFocused,
          'html-editor--with-toolbar': editor && !readOnly,
          'html-editor--flat': !outlined,
          'html-editor--dense': dense,
          'html-editor--no-overflow': noOverflow,
          'html-editor--dragover': isDragOver
        }"
        @click.native="onEditorClick"
        @dragover.native="onDragOver"
        @dragleave.native="onDragLeave"
        @drop.native="onDrop"
      />

      <!-- Floating formatting menu shown on text selection. -->
      <div v-show="editor && !readOnly" ref="bubbleMenu" class="html-bubble-menu">
        <v-btn
          icon
          x-small
          tabindex="-1"
          :title="$t('components.utility.MarkdownEditor.bold')"
          :class="{ 'toolbar-btn--active': editor && editor.isActive('bold') }"
          @mousedown.prevent
          @click="editor.chain().focus().toggleBold().run()"
        >
          <v-icon small>mdi-format-bold</v-icon>
        </v-btn>
        <v-btn
          icon
          x-small
          tabindex="-1"
          :title="$t('components.utility.MarkdownEditor.italic')"
          :class="{ 'toolbar-btn--active': editor && editor.isActive('italic') }"
          @mousedown.prevent
          @click="editor.chain().focus().toggleItalic().run()"
        >
          <v-icon small>mdi-format-italic</v-icon>
        </v-btn>
        <v-btn
          icon
          x-small
          tabindex="-1"
          :title="$t('components.utility.MarkdownEditor.underline')"
          :class="{ 'toolbar-btn--active': editor && editor.isActive('underline') }"
          @mousedown.prevent
          @click="editor.chain().focus().toggleUnderline().run()"
        >
          <v-icon small>mdi-format-underline</v-icon>
        </v-btn>
        <v-btn
          icon
          x-small
          tabindex="-1"
          :title="$t('components.utility.MarkdownEditor.strikethrough')"
          :class="{ 'toolbar-btn--active': editor && editor.isActive('strike') }"
          @mousedown.prevent
          @click="editor.chain().focus().toggleStrike().run()"
        >
          <v-icon small>mdi-format-strikethrough</v-icon>
        </v-btn>

        <v-divider vertical class="mx-1 toolbar-divider" />

        <v-btn
          icon
          x-small
          tabindex="-1"
          :title="$t('components.utility.HtmlEditor.link')"
          :class="{ 'toolbar-btn--active': editor && editor.isActive('link') }"
          @mousedown.prevent
          @click="setLink"
        >
          <v-icon small>mdi-link-variant</v-icon>
        </v-btn>
      </div>

      <input
        ref="imageFileInput"
        type="file"
        :accept="imageInputTypes"
        style="display: none"
        @change="onFileInputChange"
      />
    </div>
    <div
      v-if="hint && !isPlain"
      class="v-messages mt-1 ml-3"
      :class="$vuetify.theme.dark ? 'theme--dark' : 'theme--light'"
    >
      <div class="v-messages__message">{{ hint }}</div>
    </div>
    <div v-if="!isPlain && !readOnly && !hideCounter" class="d-flex mt-1 mr-1">
      <v-spacer />
      <small :class="charCount > maxLength ? 'orange--text' : 'grey--text'">{{ charCount }}/{{ maxLength }}</small>
    </div>
  </div>
</template>

<script lang="ts">
import StarterKit from "@tiptap/starter-kit";
import { AnyExtension, isTextSelection } from "@tiptap/core";
import { BubbleMenu } from "@tiptap/extension-bubble-menu";
import { Editor, EditorContent } from "@tiptap/vue-2";
import { TextStyle, Color, FontSize } from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { PartnerImageExtension, buildPartnerImageUrl } from "./tiptap/PartnerImageExtension";
import { PlaceholderToken } from "./tiptap/PlaceholderToken";
import { PartnerImageModule } from "@/store/modules/partner-image.store";
import { ReportImageType } from "@/models/Report/ReportImageType";
import { UserModule } from "@/store/modules/me-user.store";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { ResourceEnum } from "@/store/enum/authResourceEnum";
import { ConfigModule } from "@/store/modules/config";

/** Max file size for image uploads in bytes (5 MB) */
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

@Component({
  components: { EditorContent }
})
export default class HtmlEditor extends Vue {
  @Prop({ default: "" })
  value!: string;

  @Prop()
  label?: string;

  @Prop()
  hint?: string;

  @Prop({ default: false })
  readOnly!: boolean;

  @Prop({ default: true, type: Boolean })
  outlined!: boolean;

  @Prop({ default: false, type: Boolean })
  dense!: boolean;

  @Prop({ default: false, type: Boolean })
  noOverflow!: boolean;

  /** Partner ID required for image uploads. When set, enables image features. */
  @Prop()
  partnerId?: string;

  /** Tokens that currently have no resolved value — rendered as "missing" chips. */
  @Prop({ default: () => [] })
  missingTokens!: string[];

  /** Token paths offered in the placeholder dropdown (e.g. ["report.name", "custom.signature"]). */
  @Prop({ default: () => [] })
  availableTokens!: string[];

  /**
   * Resolves a token path to its value in the current context. The editor shows the already-rendered
   * email, so inserting a placeholder inserts its resolved value; when it resolves to empty the raw
   * token chip is inserted instead and `placeholder-missing` is emitted so the parent can prompt for it.
   */
  @Prop()
  resolveToken?: (token: string) => string;

  /** When true, render a plain v-textarea bound to value instead of the rich editor. */
  @Prop({ default: false, type: Boolean })
  isPlain!: boolean;

  /** Maximum number of characters that can be sent; shown as a counter below the editor. */
  @Prop({ default: 50000 })
  maxLength!: number;

  /** Hide the character counter. */
  @Prop({ default: false, type: Boolean })
  hideCounter!: boolean;

  editor: Editor | null = null;
  isFocused = false;
  isUploading = false;
  isDragOver = false;
  charCount = 0;
  readonly colors = [
    "#424242",
    "#4283FF",
    "#3BC8C1",
    "#4caf50",
    "#81C784",
    "#FF9800",
    "#FFB74D",
    "#f44336",
    "#7E57C2",
    "#9e9e9e"
  ];

  /** Guard against re-entrant updates from onUpdate → $emit → watcher */
  private updating = false;

  /** True on macOS-family platforms, used to show ⌘ instead of Ctrl in shortcut hints. */
  get isMac(): boolean {
    return typeof navigator !== "undefined" && /Mac|iP(hone|ad|od)/.test(navigator.platform);
  }

  /** Platform-specific modifier label matching Tiptap's "Mod" binding (⌘ on macOS, Ctrl elsewhere). */
  get modKey(): string {
    return this.isMac ? "⌘" : "Ctrl";
  }

  get canUploadImage(): boolean {
    if (!this.partnerId) return false;
    return UserModule.abilities.can(ActionEnum.CREATE, ResourceEnum.IMAGE, this.partnerId);
  }

  get imageInputTypes(): string {
    return ConfigModule.imageInputTypesString;
  }

  mounted() {
    if (this.isPlain) return;

    const missingTokens = () => this.missingTokens;
    const extensions: AnyExtension[] = [
      StarterKit.configure({ link: { autolink: false } }),
      TextStyle,
      Color,
      FontSize,
      Highlight.configure({ multicolor: true }),
      PlaceholderToken.configure({ isMissing: (token: string) => missingTokens().includes(token) }),
      BubbleMenu.configure({
        element: this.$refs.bubbleMenu as HTMLElement,
        shouldShow: ({ editor, state, from, to }) =>
          editor.isEditable && from !== to && isTextSelection(state.selection)
      })
    ];

    if (this.partnerId) {
      extensions.push(PartnerImageExtension.configure({ inline: true }));
    }

    this.charCount = (this.value || "").length;

    this.editor = new Editor({
      content: this.value || "",
      extensions,
      editable: !this.readOnly,
      onUpdate: ({ editor }) => {
        this.updating = true;
        const html = editor.getHTML();
        this.charCount = html.length;
        this.$emit("input", html);
        this.$nextTick(() => {
          this.updating = false;
        });
      },
      onFocus: () => {
        this.isFocused = true;
      },
      onBlur: () => {
        this.isFocused = false;
      }
    });
  }

  insertPlaceholder(token: string) {
    if (!this.editor) return;
    const resolved = this.resolveToken ? this.resolveToken(token) : "";
    if (resolved) {
      this.editor.chain().focus().insertContent(resolved).run();
      return;
    }
    // No value yet (e.g. custom.*/setting.*): insert the raw token chip and ask the parent to fill it.
    this.editor.chain().focus().insertContent({ type: "placeholderToken", attrs: { token } }).run();
    this.$emit("placeholder-missing", token);
  }

  tokenLabel(tokenPath: string): string {
    const prefix = "components.template.editor.placeholder.path";
    const translation = this.$t(`${prefix}.${tokenPath}`).toString();
    return translation.startsWith(prefix) ? tokenPath : translation;
  }

  /** Toggle a link on the current selection, prompting for the URL. */
  setLink() {
    if (!this.editor) return;
    const previous = this.editor.getAttributes("link").href as string | undefined;
    const raw = window.prompt(this.$t("components.utility.HtmlEditor.link").toString(), previous || "");
    if (raw === null) return;
    const href = raw.trim();
    if (href === "") {
      this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    if (!/^(https?:\/\/|mailto:)/i.test(href)) {
      this.$toast.error(this.$t("components.utility.HtmlEditor.invalidLink").toString());
      return;
    }
    this.editor.chain().focus().toggleLink({ href }).run();
  }

  /** Open the native file picker for image upload */
  triggerImageUpload() {
    (this.$refs.imageFileInput as HTMLInputElement)?.click();
  }

  /** Handle file selection from the file input */
  onFileInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.uploadAndInsertImage(file);
    }
    input.value = "";
  }

  /* ---------- Drag & Drop ---------------------------------------- */

  onDragOver(event: DragEvent) {
    if (event.dataTransfer?.types?.includes("Files")) {
      event.preventDefault();
      this.isDragOver = true;
    }
  }

  onDragLeave() {
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    this.isDragOver = false;
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (!files.length) return;
    // Always stop the browser from navigating to/opening the dropped file, even when we can't
    // upload images (no partnerId/permission) or there are no non-image files to forward.
    event.preventDefault();
    const images = files.filter((f) => f.type.startsWith("image/"));
    const others = files.filter((f) => !f.type.startsWith("image/"));
    if (images.length && this.canUploadImage) {
      images.forEach((f) => this.uploadAndInsertImage(f));
    }
    if (others.length) {
      this.$emit("files-dropped", others);
    }
  }

  /* ---------- Upload & Insert ------------------------------------ */

  async uploadAndInsertImage(file: File) {
    if (!this.canUploadImage || !this.partnerId || !this.editor) return;

    if (file.size > MAX_IMAGE_SIZE) {
      this.$toast.error(this.$t("components.utility.MarkdownEditor.imageTooLarge").toString());
      return;
    }

    if (!file.type.startsWith("image/")) {
      this.$toast.error(this.$t("components.utility.MarkdownEditor.invalidImageType").toString());
      return;
    }

    this.isUploading = true;
    try {
      const image = await PartnerImageModule.create({
        partnerId: this.partnerId,
        file,
        type: ReportImageType.others
      });

      this.editor
        .chain()
        .focus()
        .setImage({ src: buildPartnerImageUrl(image.id, this.partnerId), alt: file.name })
        .run();
    } catch (error) {
      this.$log.error("Image upload failed", error);
      this.$toast.error(this.$t("components.utility.MarkdownEditor.uploadFailed").toString());
    } finally {
      this.isUploading = false;
    }
  }

  onEditorClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const chip = target.closest("[data-placeholder-token]") as HTMLElement | null;
    if (!chip) return;
    const token = chip.getAttribute("data-placeholder-token");
    if (token) this.$emit("token-click", token);
  }

  @Watch("missingTokens")
  onMissingTokensChange() {
    if (!this.editor) return;
    const { from, to } = this.editor.state.selection;
    const html = this.editor.getHTML();
    this.editor.commands.setContent(html, false);
    this.editor.commands.setTextSelection({ from, to });
  }

  @Watch("value")
  onValueChange(val: string) {
    if (this.updating || !this.editor) return;
    const current = this.editor.getHTML();
    if (val !== current) {
      this.editor.commands.setContent(val || "", false);
      this.charCount = (val || "").length;
    }
  }

  @Watch("readOnly")
  onReadOnlyChange(val: boolean) {
    this.editor?.setEditable(!val);
  }

  beforeDestroy() {
    this.editor?.destroy();
  }
}
</script>

<style scoped>
.html-toolbar {
  border: thin solid rgba(0, 0, 0, 0.38);
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  padding: 6px 4px;
  gap: 2px;
}

.theme--dark .html-toolbar {
  border-color: rgba(255, 255, 255, 0.24);
}

.html-toolbar.html-toolbar--focused {
  border-color: var(--v-primary-base, #1976d2);
  border-width: 2px 2px 0 2px;
  padding: 6px 4px;
}

.html-toolbar >>> .v-btn:hover,
.html-bubble-menu >>> .v-btn:hover {
  background-color: rgba(0, 0, 0, 0.06);
}

.theme--dark .html-toolbar >>> .v-btn:hover,
.theme--dark .html-bubble-menu >>> .v-btn:hover {
  background-color: rgba(255, 255, 255, 0.12);
}

.toolbar-btn--active {
  background-color: rgba(0, 0, 0, 0.1);
}

.theme--dark .toolbar-btn--active {
  background-color: rgba(255, 255, 255, 0.18);
}

.html-toolbar >>> .toolbar-btn--active:hover,
.html-bubble-menu >>> .toolbar-btn--active:hover {
  background-color: rgba(0, 0, 0, 0.14);
}

.theme--dark .html-toolbar >>> .toolbar-btn--active:hover,
.theme--dark .html-bubble-menu >>> .toolbar-btn--active:hover {
  background-color: rgba(255, 255, 255, 0.24);
}

.toolbar-divider {
  max-height: 20px;
  align-self: center;
}

/* Floating selection menu — the BubbleMenu extension toggles visibility/opacity and
   sets position/left/top inline, so default to hidden + out of flow to avoid a load flash. */
.html-bubble-menu {
  display: flex;
  align-items: center;
  visibility: hidden;
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 20;
  gap: 2px;
  padding: 2px 4px;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: opacity 0.12s;
}

.theme--dark .html-bubble-menu {
  background-color: rgb(40, 40, 40);
  border-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
}

.html-editor--with-toolbar {
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
}

.html-editor {
  border: thin solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  padding: 10px 12px;
  min-height: 150px;
  font-family: "Roboto", sans-serif;
  font-size: 16px;
  line-height: 1.75rem;
  letter-spacing: normal;
  color: rgba(0, 0, 0, 0.87);
  transition: border 0.15s;
}

.theme--dark .html-editor {
  border-color: rgba(255, 255, 255, 0.24);
  color: rgba(255, 255, 255, 0.87);
}

.html-editor-control:hover .html-toolbar:not(.html-toolbar--focused),
.html-editor-control:hover .html-editor:not(.html-editor--focused):not(.html-editor--readonly) {
  border-color: rgba(0, 0, 0, 0.86);
}

.theme--dark .html-editor-control:hover .html-toolbar:not(.html-toolbar--focused),
.theme--dark .html-editor-control:hover .html-editor:not(.html-editor--focused):not(.html-editor--readonly) {
  border-color: rgba(255, 255, 255, 0.86);
}

.html-editor.html-editor--focused {
  border-color: var(--v-primary-base, #1976d2);
  border-width: 2px;
  padding: 9px 11px;
}

.html-editor--readonly {
  border-color: rgba(0, 0, 0, 0.12);
  padding: 0;
}

.html-editor--flat {
  border: none;
}

.html-editor-label {
  font-size: 12px;
  margin-bottom: 4px;
}

.html-editor--readonly >>> .ProseMirror a {
  pointer-events: auto;
  cursor: pointer;
}

.theme--dark .html-editor--readonly {
  border-color: rgba(255, 255, 255, 0.12);
}

.html-editor >>> .ProseMirror {
  outline: none;
  min-height: 134px;
}

.html-editor >>> .ProseMirror p {
  margin: 0 0 10px 0;
}

/** dense: remove all spacing and min-height — must come after base .ProseMirror rules */
.html-editor--dense {
  padding: 0;
  min-height: unset;
}

.html-editor--dense >>> div.ProseMirror {
  padding: 0 !important;
  margin: 0 !important;
  min-height: unset !important;
}

.html-editor--dense >>> div.ProseMirror p:last-child {
  margin-bottom: 0;
}

.html-editor >>> .ProseMirror h1 {
  font-size: 1.6em;
  font-weight: 600;
  margin: 24px 0 16px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding-bottom: 0.3rem;
  line-height: 0.8;
}

.theme--dark .html-editor >>> .ProseMirror h1 {
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

.html-editor >>> .ProseMirror h2 {
  font-size: 1.35em;
  font-weight: 600;
  margin: 24px 0 16px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding-bottom: 0.3rem;
  line-height: 1;
}

.theme--dark .html-editor >>> .ProseMirror h2 {
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

.html-editor >>> .ProseMirror h3 {
  font-size: 1.15em;
  font-weight: 600;
  line-height: 1.5;
  margin: 10px 0 6px 0;
}

.html-editor >>> .ProseMirror h4,
.html-editor >>> .ProseMirror h5,
.html-editor >>> .ProseMirror h6 {
  font-size: 1em;
  font-weight: 600;
  line-height: 1.5;
  margin: 10px 0 4px 0;
}

.html-editor >>> .ProseMirror ul,
.html-editor >>> .ProseMirror ol {
  padding-left: 24px;
  margin: 0 0 10px 0;
}

.html-editor >>> .ProseMirror li {
  margin-bottom: 2px;
  line-height: 1.2;
}

.html-editor >>> .ProseMirror li > p {
  margin: 0;
}

.html-editor >>> .ProseMirror blockquote {
  border-left: 3px solid rgba(0, 0, 0, 0.12);
  margin: 0 0 10px 0;
  padding-left: 12px;
  color: rgba(0, 0, 0, 0.6);
  line-height: 1.5;
}

.html-editor >>> .ProseMirror blockquote p {
  margin-bottom: 0;
}

.theme--dark .html-editor >>> .ProseMirror blockquote {
  border-left-color: rgba(255, 255, 255, 0.24);
  color: rgba(255, 255, 255, 0.6);
}

.html-editor >>> .ProseMirror hr {
  border: none;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  margin: 0 0 10px 0;
}

.theme--dark .html-editor >>> .ProseMirror hr {
  border-top-color: rgba(255, 255, 255, 0.24);
}

/* Drag-over visual feedback */
.html-editor--dragover {
  border-color: var(--v-primary-base, #1976d2) !important;
  border-style: dashed !important;
  background-color: var(--v-primary-lighten5, rgba(25, 118, 210, 0.06));
}

/* noOverflow: strip overflow from ProseMirror entirely */
.html-editor--no-overflow >>> .ProseMirror {
  overflow: unset !important;
}

/* Partner image styles inside editor */
.html-editor >>> .ProseMirror img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 4px 0;
}

/* Placeholder token chip styles */
.html-editor >>> .placeholder-token {
  background-color: var(--v-primary-lighten5, rgba(25, 118, 210, 0.12));
  color: var(--v-primary-base, #1976d2);
  border-radius: 3px;
  padding: 0 3px;
  white-space: nowrap;
}
.html-editor >>> .placeholder-token--missing {
  background-color: rgba(244, 67, 54, 0.12);
  color: #d32f2f;
  border: 1px dashed #d32f2f;
  cursor: pointer;
}

/* Mobile: horizontally scrollable toolbar with larger touch targets */
@media (max-width: 600px) {
  .html-toolbar {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .html-toolbar::-webkit-scrollbar {
    height: 3px;
  }

  .html-toolbar >>> .v-btn--icon.v-size--x-small {
    height: 36px;
    width: 36px;
  }

  .html-toolbar >>> .v-btn--icon.v-size--x-small .v-icon {
    font-size: 20px !important;
  }

  .html-toolbar .v-spacer {
    min-width: 8px;
  }
}
</style>
